import { BusinessType } from '../data-layer/business/types';
import { UserModel } from '../data-layer/user/types';
import { Role } from '../mappers/business/toBusinessModel';
import { BusinessRegistrationStatus } from '../mappers/users/toUserModel';
import { BusinessUI } from '../ui/business/types';
import { PostUI } from '../ui/posts/types';
import { UserUI } from '../ui/users/types';

export const isBlogger = (role?: Role) => role === Role.BLOGGER;

export const isAdmin = (role?: Role) => role === Role.APPROVED_ADMIN || isBlogger(role);

export const isSuperAdmin = (role?: Role) => role === Role.SUPER_ADMIN;

export const isDeclinedAdmin = (role?: Role) => role === Role.DECLINED_BUSINESS_ADMIN;

export const isSupport = (role?: Role) => role === Role.SUPPORT;

export const hasBaseProfile = (currentUser?: UserModel) =>
    !currentUser?.profile || (currentUser?.profile && !isBlogger(currentUser.role));

export const canRegisterBusiness = (status?: BusinessRegistrationStatus) =>
    status === BusinessRegistrationStatus.DECLINED ||
    status === BusinessRegistrationStatus.NOT_REGISTERED;

export const canNotRegisterBusiness = (status?: BusinessRegistrationStatus) =>
    status === BusinessRegistrationStatus.REGISTERED;

export const isBusinessOwner = (businessId?: string, user?: UserUI) =>
    isAdmin(user?.role) &&
    (businessId === user?.profile?.id || businessId === user?.profile?.publicId);

export const canEditBusiness = (businessId?: string, user?: UserUI) =>
    isSuperAdmin(user?.role) || isBusinessOwner(businessId, user);

export const canSeeSupportChat = (businessId?: string, user?: UserUI) =>
    canEditBusiness(businessId, user) || isDeclinedAdmin();

export const isBusinessAdmin = (businessId?: string, user?: UserUI) =>
    (isAdmin(user?.role) && businessId === user?.profile?.id) ||
    isSuperAdmin(user?.role) ||
    isSupport(user?.role);

export const canEditBusinessPublicId = (role?: Role) => role === Role.SUPER_ADMIN;

export const canEditBusinessInstagramUrl = (role?: Role) => role === Role.SUPER_ADMIN;

export const canEditBusinessSiteUrl = (role?: Role) => role === Role.SUPER_ADMIN;

export const canEditBusinessCategories = (role?: Role) => role === Role.SUPER_ADMIN;

export const isBloggerBusiness = (businessType?: BusinessType) =>
    businessType === BusinessType.BLOGGER;

export const isBloggerOwner = (business?: BusinessUI, user?: UserUI) =>
    isBusinessAdmin(business?.id, user) && isBlogger(user?.role);

export const isPublicIdVisible = (businessType?: BusinessType) => !isBloggerBusiness(businessType);

export const isSiteVisible = (businessType?: BusinessType) => !isBloggerBusiness(businessType);

export const hasAccessToPostStat = (businessId?: string, user?: UserUI, post?: PostUI) =>
    (canEditBusiness(businessId, user) || isSupport(user?.role)) && post?.postType !== 'product';

export const hasAccessToBusinessStat = (businessId?: string, user?: UserUI) =>
    canEditBusiness(businessId, user) || isSupport(user?.role);

export const canSaveOrUpdatePost = (businessId?: string, user?: UserUI) =>
    isBusinessAdmin(businessId, user);

export const canSavePostToDraft = (businessId?: string, user?: UserUI) =>
    isBusinessAdmin(businessId, user);

export const canViewBusinessAsAdmin = (businessId?: string, user?: UserUI) =>
    isSuperAdmin(user?.role) ||
    isSupport(user?.role) ||
    (isAdmin(user?.role) && businessId === user?.profile?.id);

export const canEditBusinessCover = (business: BusinessUI, user?: UserUI) =>
    canEditBusiness(business.id, user) && !isBloggerBusiness(business.type);

export const canEditBusinessAvatar = (businessId: string, user?: UserUI) =>
    canEditBusiness(businessId, user);

export const canSeeBusinessSettings = (businessId: string, user?: UserUI) =>
    canEditBusiness(businessId, user) || isSupport(user?.role);

export const hasAccessToFeedManagement = (businessId: string, user?: UserUI) =>
    businessId &&
    user &&
    (user.profile?.publicId === businessId || isSuperAdmin(user.role) || isSupport(user.role));

export const canShowCpa = (currentUser?: UserModel, business?: BusinessUI) => {
    // Показываем только блогерам, пользователям(base), супер админам. Не показываем бизнесам
    if (
        !currentUser ||
        !(
            currentUser.role === Role.BASE ||
            isBlogger(currentUser.role) ||
            isSuperAdmin(currentUser.role)
        )
    ) {
        return false;
    }

    if (business?.type === BusinessType.BUSINESS) {
        return false;
    }

    if (currentUser.role === Role.BASE) {
        return !business;
    }

    if (isBlogger(currentUser.role)) {
        return (
            business && isBloggerBusiness(business.type) && isBloggerOwner(business, currentUser)
        );
    }

    if (isSuperAdmin(currentUser.role)) {
        return !business;
    }

    return false;
};
